import * as React from 'react';
import {DayPicker} from 'react-day-picker';
import {ChevronLeft, ChevronRight} from 'lucide-react';
import {cn} from '../lib/cn';
import {calendarClassNames} from './calendarStyles';

type CalendarClassNames = typeof calendarClassNames;
type CalendarClassNamesKeys = keyof CalendarClassNames;

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function getMergedClassNames(
  defaultClassNames: CalendarClassNames,
  userClassNames?: Partial<CalendarClassNames>,
): CalendarClassNames {
  return Object.keys(defaultClassNames).reduce(
    (acc, key) => ({
      ...acc,
      [key]: userClassNames?.[key as CalendarClassNamesKeys]
        ? cn(
            defaultClassNames[key as CalendarClassNamesKeys],
            userClassNames[key as CalendarClassNamesKeys],
          )
        : defaultClassNames[key as CalendarClassNamesKeys],
    }),
    {} as CalendarClassNames,
  );
}

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  components: userComponents,
  ...props
}: CalendarProps) {
  const mergedClassNames = getMergedClassNames(calendarClassNames, classNames);

  const defaultComponents = {
    Chevron: (iconProps: {orientation?: string} & React.SVGProps<SVGSVGElement>) => {
      if (iconProps.orientation === 'left') {
        return (
          <ChevronLeft size={16} strokeWidth={2} {...iconProps} aria-hidden="true" />
        );
      }

      return <ChevronRight size={16} strokeWidth={2} {...iconProps} aria-hidden="true" />;
    },
  };

  return (
    <DayPicker
      data-slot="calendar"
      showOutsideDays={showOutsideDays}
      className={cn('w-fit', className)}
      classNames={mergedClassNames}
      components={{
        ...defaultComponents,
        ...userComponents,
      }}
      {...props}
    />
  );
}

Calendar.displayName = 'Calendar';
